import { useState, useCallback, useEffect } from 'react';

// Type definitions
export interface VoiceRequest {
  voice_id: string;
  text: string;
}

export interface AvatarLipSyncRequest {
  voice_id: string;
  audio_url: string;
  video_url: string;
  image_url: string;
  text: string;
  duration: number;
  user_id?: string;
  aspect_ratio?: string;
}

export interface ContactRequest {
  company: string;
  email: string;
  message: string;
  name: string;
}

export interface ApiResponse {
  data: {
    id: string;
    status: 'completed' | 'pending' | 'succeeded';
    text: string;
    audio_url: string | null;
  };
}

// API base URL - can be configured via environment variable
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL
// Polling interval in milliseconds
const POLLING_INTERVAL = 2000;

/**
 * API Client for voice and avatar lip sync requests
 */
export class ApiClient {
  /**
   * Create a voice synthesis request
   * @param voiceRequest Voice request parameters
   * @returns API response
   */
  static async createVoice(voiceRequest: VoiceRequest): Promise<ApiResponse> {
    const response = await fetch(`${API_BASE_URL}/api/voice`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ voice: voiceRequest }),
    });

    if (!response.ok) {
      throw new Error(`Voice API error: ${response.status}`);
    }

    return response.json();
  }

  static async createContact(contactRequest: ContactRequest): Promise<ApiResponse> {
    const response = await fetch(`${API_BASE_URL}/api/contact`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ contact: contactRequest }),
    });

    if (!response.ok) {
      throw new Error(`Voice API error: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Create an avatar lip sync request
   * @param avatarRequest Avatar lip sync request parameters
   * @returns API response
   */
  static async createAvatarLipSync(avatarRequest: AvatarLipSyncRequest): Promise<ApiResponse> {
    const response = await fetch(`${API_BASE_URL}/api/avatars/lip_sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ avatar: avatarRequest }),
    });

    if (!response.ok) {
      throw new Error(`Avatar lip sync API error: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Get the status of a request
   * @param requestId Request ID
   * @returns API response
   */
  static async getRequest(requestId: string): Promise<ApiResponse> {
    const response = await fetch(`${API_BASE_URL}/api/requests/${requestId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Get request API error: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Poll a request until it's completed
   * @param requestId Request ID
   * @param onProgress Optional callback for progress updates
   * @returns Completed API response
   */
  static async pollUntilCompleted(
    requestId: string,
    onProgress?: (response: ApiResponse) => void
  ): Promise<ApiResponse> {
    let response: ApiResponse;
    
    do {
      response = await this.getRequest(requestId);
      if (onProgress) {
        onProgress(response);
      }
      
      if (response.data.status === 'pending') {
        await new Promise(resolve => setTimeout(resolve, POLLING_INTERVAL));
      }
    } while (response.data.status === 'pending');
    
    return response;
  }
}

/**
 * React hook for creating and tracking voice requests
 */
export function useVoiceRequest() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [response, setResponse] = useState<ApiResponse | null>(null);
  const [completed, setCompleted] = useState(false);

  const createRequest = useCallback(async (voiceRequest: VoiceRequest) => {
    setLoading(true);
    setError(null);
    setCompleted(false);
    
    try {
      // Create the voice request
      const initialResponse = await ApiClient.createVoice(voiceRequest);
      setResponse(initialResponse);

      // Poll until completed
      const completedResponse = await ApiClient.pollUntilCompleted(
        initialResponse.data.id,
        progress => setResponse(progress)
      );

      setResponse(completedResponse);
      setCompleted(true);
      return completedResponse;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    createVoiceRequest: createRequest,
    loading,
    error,
    response,
    completed,
  };
}

export function useContactRequest() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [response, setResponse] = useState<ApiResponse | null>(null);
  const [completed, setCompleted] = useState(false);

  const createRequest = useCallback(async (contactRequest: ContactRequest) => {
    setLoading(true);
    setError(null);
    setCompleted(false);
    
    try {
      // Create the voice request
      const response = await ApiClient.createContact(contactRequest);
      setResponse(response);
      setCompleted(true);
      return response;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    createContactRequest: createRequest,
    loading,
    error,
    response,
    completed,
  };
}

/**
 * React hook for creating and tracking avatar lip sync requests
 */
export function useAvatarLipSyncRequest() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [response, setResponse] = useState<ApiResponse | null>(null);
  const [completed, setCompleted] = useState(false);

  const createRequest = useCallback(async (avatarRequest: AvatarLipSyncRequest) => {
    setLoading(true);
    setError(null);
    setCompleted(false);
    
    try {
      // Create the avatar lip sync request
      const initialResponse = await ApiClient.createAvatarLipSync(avatarRequest);
      localStorage.setItem('requestId', initialResponse?.data?.id)

      setResponse(initialResponse);
      
      // Poll until completed
      const completedResponse = await ApiClient.pollUntilCompleted(
        initialResponse.data.id,
        progress => setResponse(progress)
      );
      
      setResponse(completedResponse);
      setCompleted(true);
      return completedResponse;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    createAvatarLipSyncRequest: createRequest,
    loading,
    error,
    response,
    completed,
  };
}

/**
 * React hook for tracking an existing request
 */
export function useRequestStatus(requestId: string | null) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [response, setResponse] = useState<ApiResponse | null>(null);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    if (!requestId) return;
    
    let isMounted = true;
    setLoading(true);
    setError(null);
    setCompleted(false);
    
    const pollRequest = async () => {
      try {
        const completedResponse = await ApiClient.pollUntilCompleted(
          requestId,
          progress => {
            if (isMounted) {
              setResponse(progress);
            }
          }
        );
        
        if (isMounted) {
          setResponse(completedResponse);
          setCompleted(true);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    pollRequest();
    
    return () => {
      isMounted = false;
    };
  }, [requestId]);

  return {
    loading,
    error,
    response,
    completed,
  };
}
